import * as React from "react";
import { Eye, Navigation, CheckCircle, XCircle, Edit, Check, X } from "lucide-react";
import { ExtractedDataField } from "../../../../types/dataTypes";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface FieldRowProps {
  field: ExtractedDataField;
  schemaId: string;
  onView?: (field: ExtractedDataField) => void;
  onMapToWord?: (field: ExtractedDataField) => void;
  onValidate?: (fieldId: string, isValid: boolean | null) => void;
  onFieldUpdate?: (fieldId: string, newValue: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

const FieldRow: React.FC<FieldRowProps> = ({ 
  field, 
  schemaId: _schemaId,
  onView, 
  onMapToWord,
  onValidate,
  onFieldUpdate,
  className = '',
  style
}) => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(field.correctedValue || field.value || '');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  React.useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleValidation = (isValid: boolean | null) => {
    if (onValidate) {
      onValidate(field.id, isValid);
    }
  };

  const handleEdit = () => {
    setEditValue(field.correctedValue || field.value || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    if (onFieldUpdate && editValue !== (field.correctedValue || field.value)) {
      onFieldUpdate(field.id, editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(field.correctedValue || field.value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const getValidationIcon = () => {
    const iconClass = "w-3.5 h-3.5 md:w-4 md:h-4";
    if (field.isValidated === true) {
      return <CheckCircle className={cn(iconClass, "text-emerald-500")} />;
    } else if (field.isValidated === false) {
      return <XCircle className={cn(iconClass, "text-red-500")} />;
    }
    return null;
  };

  const getValidationButtonClass = (isValid?: boolean) => {
    // Base classes that Button component will apply + overrides
    // We use variant="outline" or "ghost" as base but override colors heavily
    
    const baseClass = "h-7 w-7 md:h-8 md:w-8 p-0 border";
    
    if (field.isValidated === isValid) {
      if (isValid === true) {
        return cn(baseClass, "bg-emerald-50 border-emerald-500 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800");
      } else if (isValid === false) {
        return cn(baseClass, "bg-red-50 border-red-500 text-red-700 hover:bg-red-100 hover:text-red-800");
      }
    }
    return cn(baseClass, "bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900");
  };

  const fieldValue = field.correctedValue || field.value || '-';
  const isLongText = fieldValue.length > 50;

  return (
    <div 
      className={cn(
        "flex flex-col gap-3 p-3 bg-white border border-slate-200 rounded-lg shadow-sm transition-all mb-3 hover:border-slate-300 hover:shadow-md md:gap-3.5 md:p-4 group",
        className
      )}
      style={style}
    >
      {/* Field Header */}
      <div className="flex items-start justify-between gap-2">
        <h5 className="text-[13px] font-semibold text-slate-800 m-0 flex-1 leading-tight md:text-sm">{field.name}</h5>
        <div className="flex items-center justify-center w-[18px] h-[18px] md:w-5 md:h-5">
          {getValidationIcon()}
        </div>
      </div>

      {/* Field Content */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-4">
        {/* Field Value - Editable */}
        <div className="flex-1 w-full relative">
          {isEditing ? (
            <div className="relative">
              <Textarea
                ref={textareaRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label={`Edit value for field ${field.name}`}
                placeholder="Enter field value..."
                className="w-full min-h-[60px] md:min-h-[80px] max-h-[200px] p-2 md:p-3 text-xs md:text-[13px] border-2 border-gray-700 font-mono leading-relaxed resize-y focus-visible:ring-0"
              />
              <div className="absolute -top-2 right-2 flex gap-1 bg-white px-1">
                <Button
                  onClick={handleSave}
                  size="icon"
                  className="h-5 w-5 rounded bg-emerald-500 hover:bg-emerald-600 text-white"
                  title="Save (Enter)"
                >
                  <Check className="w-3 h-3" />
                </Button>
                <Button
                  onClick={handleCancel}
                  size="icon"
                  className="h-5 w-5 rounded bg-red-500 hover:bg-red-600 text-white"
                  title="Cancel (Escape)"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className={cn(
                "relative text-xs text-slate-600 bg-slate-50 p-2.5 rounded-md border border-slate-200 font-mono leading-relaxed w-full min-h-[40px] break-words whitespace-pre-wrap md:text-[13px] cursor-pointer group/value hover:border-slate-300 transition-colors",
                !isLongText && "truncate"
              )}
              onClick={handleEdit}
              title={fieldValue}
            >
              {fieldValue}
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit();
                }}
                variant="ghost"
                size="icon"
                className="absolute top-1 right-1 h-4 w-4 rounded bg-slate-100 opacity-0 group-hover/value:opacity-100 transition-opacity hover:bg-slate-200 text-slate-500"
                title="Edit text"
              >
                <Edit className="w-2.5 h-2.5" />
              </Button>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap md:gap-2 md:flex-nowrap">
          {/* Validation Section */}
          <div className="flex items-center gap-1.5 md:gap-2">
            <Button
              onClick={() => handleValidation(true)}
              title="Mark as valid"
              variant="outline"
              className={getValidationButtonClass(true)}
            >
              <CheckCircle className={cn("w-3.5 h-3.5 md:w-4 md:h-4")} />
            </Button>
            
            <Button
              onClick={() => handleValidation(false)}
              title="Mark as invalid"
              variant="outline"
              className={getValidationButtonClass(false)}
            >
              <XCircle className={cn("w-3.5 h-3.5 md:w-4 md:h-4")} />
            </Button>
          </div>

          {/* Action Buttons */}
          {onView && (
            <Button
              onClick={() => onView(field)}
              title="View in document"
              variant="outline"
              size="icon"
              className="h-7 w-7 md:h-8 md:w-8 border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            >
              <Eye className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </Button>
          )}
          
          {onMapToWord && (
            <Button
              onClick={() => onMapToWord(field)}
              title="Map to Word"
              className="bg-gray-700 text-white border-gray-700 h-7 md:h-8 px-3 md:px-4 text-xs md:text-sm font-medium hover:bg-gray-800"
            >
              <Navigation className="w-3 h-3 md:w-3.5 md:h-3.5 mr-1" />
              <span className="hidden md:inline">Map</span>
              <span className="md:hidden">Map</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldRow;
